import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CreateRealPropertyRequest,
  RealPropertyListItem,
  RealPropertyListQuery,
  UpdateRealPropertyRequest,
} from './realPropertyTypes';

/**
 * Serializes the base list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned, which is the opposite result.
 */
function base(query: ListQuery, params: URLSearchParams): URLSearchParams {
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

function propertyQuery(query: RealPropertyListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.tenure !== undefined) params.set('tenure', query.tenure);
  if (query.category !== undefined) params.set('category', query.category);
  if (query.type !== undefined) params.set('type', query.type);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * The real-property register — one row per ACTUAL building or leased space.
 *
 * **There is no `clear`**, unlike every other list in this suite. Clearing a register of
 * buildings is not the administrative tidy-up of an over-filled vocabulary that Clear data
 * exists for; the module declares no key for it and the server has no endpoint.
 */
export const realPropertiesApi = {
  list: (query: RealPropertyListQuery, signal?: AbortSignal) =>
    api<PagedResult<RealPropertyListItem>>(`/api/real-properties${propertyQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<RealPropertyListItem>(`/api/real-properties/${id}`, { signal }),

  /** Registers one actual property under a real-property catalog type. */
  create: (request: CreateRealPropertyRequest) =>
    api<RealPropertyListItem>('/api/real-properties', { method: 'POST', body: request }),

  update: (id: string, request: UpdateRealPropertyRequest) =>
    api<RealPropertyListItem>(`/api/real-properties/${id}`, { method: 'PUT', body: request }),

  /** Refuses nothing — a property holds no stock, so nothing is stranded by hiding one. */
  deactivate: (id: string) => api<void>(`/api/real-properties/${id}`, { method: 'DELETE' }),

  /**
   * Restore, which OWES a clash check and may therefore 409.
   *
   * The code's unique index is filtered on `IsActive`, so deleting a property freed its code and
   * something live may hold it now.
   */
  activate: (id: string) =>
    api<RealPropertyListItem>(`/api/real-properties/${id}/restore`, { method: 'POST' }),
};
